import { useState, useContext } from 'react';
import axios from '@helpers/axios';
import { v4 as uuidv4 } from 'uuid';
import { GameDetails } from '@theme/theme';
import { GameStartEvent } from '@features/Schedule/schedule.d';
import { TeamGameResponse } from '@dto/api';
import { TeamContext } from '@services/Session/context';
import { formatTsToDayYearDate } from '@helpers/dateUtils';
import useEventsGameStart from '@hooks/useEventsGameStart';
import { useHistory } from 'react-router-dom';

const useGame = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gameCreated, setGameCreated] = useState<TeamGameResponse | null>(null);
  const teamStore = useContext(TeamContext);
  const { trackGameStartEvent } = useEventsGameStart(); //
  const history = useHistory();

  const createGame = async (
    gameDetails: GameDetails,
    teamId: string,
    teamAttribSportType: string
  ): Promise<TeamGameResponse> => {
    setLoading(true);
    setError(null);

    try {
      const scoreDelegationKey = localStorage.getItem('scoreDelegationKey');
      const scoreGameId = `GmSs01-${uuidv4()}`;
      const payload = {
        ...gameDetails,
        scoreGameId,
      };
      const config = scoreDelegationKey
        ? { headers: { 'X-Slhd-Scoring-Key': scoreDelegationKey } }
        : {};
      const response = await axios.post<TeamGameResponse>(
        '/v2/team_games',
        payload,
        config
      );
      setGameCreated(response.data);
      teamStore.updateSchedule(response.data);
      trackGameStartEvent(GameStartEvent.GAME_CREATION_COMPLETED, {
        teamId,
        gameId: response.data.id,
        date: formatTsToDayYearDate(response.data.createdTs),
        sport: teamAttribSportType,
        opponent_name: response.data.scoreOpponentName,
        sanctioning_partner: gameDetails?.sanctioningPartner,
      });
      return response.data;
    } catch (err: unknown) {
      setError('Failed to create the game');
      throw new Error('Failed to create the game');
    } finally {
      setLoading(false);
    }
  };

  const updateGame = async (
    teamId: string,
    gameId: string,
    gameDetails: GameDetails
  ): Promise<TeamGameResponse> => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...gameDetails,
      };
      const scoreDelegationKey = localStorage.getItem('scoreDelegationKey');
      const config = scoreDelegationKey
        ? { headers: { 'X-Slhd-Scoring-Key': scoreDelegationKey } }
        : {};

      const response = await axios.post<TeamGameResponse>(
        `/v2/teams/${teamId}/games/${gameId}`,
        payload,
        config
      );
      teamStore.updateGame(response.data);
      return response.data;
    } catch (err: unknown) {
      setError('Failed to update the game');
      throw new Error('Failed to update the game');
    } finally {
      setLoading(false);
    }
  };

  const deleteGame = async (teamId: string, gameId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.delete(
        `/v2/teams/${teamId}/games/${gameId}`
      );
      if (response.status === 204) {
        teamStore.deleteGame(gameId);
      }
      return response.data;
    } catch (err: unknown) {
      setError('Failed to delete the game');
      throw new Error('Failed to delete the game');
    } finally {
      setLoading(false);
    }
  };

  const endGame = async (teamId: string, gameId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `/v2/teams/${teamId}/games/${gameId}/end`
      );
      teamStore.updateGame(response.data);
      return response.data;
    } catch (err: unknown) {
      setError('Failed to end the game');
      throw new Error('Failed to end the game');
    } finally {
      setLoading(false);
    }
  };

  const getScoreGameUrl = async (
    teamId: string,
    game: TeamGameResponse
  ): Promise<void> => {
    localStorage.setItem('scoreTeamId', teamId);
    const hasLineUp = game?.scoreOurLineup || [];
    const quickStartMode = hasLineUp?.length > 0 ? false : true;
    const body = {
      teamId: game.DNOR_teamId,
      teamGameId: game.id,
      teamGameUpdate: {
        isQuickStartMode: quickStartMode,
      },
    };

    const resp = await axios.put('/api/team_game', body);
    if (resp.status === 200) {
      switch (game.sportType) {
        case 'bbsb':
          history.push(`/scoring/bbsb/score/${teamId}/${game.scoreGameId}`);
          break;
        case 'volleyball':
          history.push(`/scoring/vb/score/${teamId}/${game.scoreGameId}`);
          break;
        default:
          history.push(`/scoring/generic/score/${teamId}/${game.scoreGameId}`);
          break;
      }
    } else {
      setError('Failed to start scoring');
      throw new Error('Failed to start scoring');
    }
  };

  const getSetLineupUrl = (teamId: string, game: TeamGameResponse): string => {
    localStorage.setItem('scoreTeamId', teamId);
    switch (game.sportType) {
      case 'bbsb':
        return `/scoring/lineups/${teamId}/${game.id}`;
      case 'volleyball':
        return `/scoring/vb_lineups/${teamId}/${game.id}`;
      default:
        return '#';
    }
  };

  return {
    createGame,
    updateGame,
    loading,
    error,
    gameCreated,
    deleteGame,
    endGame,
    getScoreGameUrl,
    getSetLineupUrl,
  };
};

export default useGame;
